import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Target, 
  Award, 
  ArrowRight, 
  Activity,
  Zap
} from 'lucide-react';

// --- Reusable UI Components ---

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-2 mb-6">
    <div className="w-8 h-[2px] bg-blue-600"></div>
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-600 italic">
      {children}
    </span>
  </div>
);

const BloombergButton = ({ children, variant = 'primary', className = '' }) => {
  const base = "px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all duration-200 border flex items-center gap-2";
  const styles = {
    primary: "bg-black text-white border-black hover:bg-zinc-800",
    outline: "bg-transparent text-black border-black hover:bg-black hover:text-white",
  };
  
  return (
    <button className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
};

const StatItem = ({ label, value }) => (
  <div className="border-t border-zinc-200 pt-6">
    <div className="text-5xl font-light tracking-tighter text-black mb-2">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</div>
  </div>
);

// --- Page Component ---

const About = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- SEO & Schema Markup ---
  useEffect(() => {
    // 1. Title & Meta Tags
    document.title = "Tentang Kami - Konveksi Bandung | Sejarah & Visi iMoonusel";
    
    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta("description", "Mengenal iMoonusel, konveksi Bandung yang berdiri sejak 2013. Kami adalah partner produksi garment terpercaya untuk seragam, kaos, dan merchandise dengan standar presisi tinggi.");
    setMeta("keywords", "tentang imoonusel, profil konveksi bandung, sejarah konveksi, visi misi konveksi, tim konveksi profesional");

    // Open Graph
    const setOg = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setOg("og:title", "Tentang Kami - Konveksi Bandung | Sejarah & Visi iMoonusel");
    setOg("og:description", "Dedikasi 10+ tahun dalam industri garment. Temukan cerita di balik kualitas produksi iMoonusel.");
    setOg("og:image", "/logo512.png");
    setOg("og:url", window.location.href);
    setOg("og:type", "website");

    // 2. JSON-LD Schema Markup
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "name": "iMoonusel Konveksi",
          "url": "https://konveksiimoonusel.com",
          "logo": "https://konveksiimoonusel.com/logo512.png",
          "foundingDate": "2013",
          "founders": [
            {
              "@type": "Person",
              "name": "Founder Name" // You can make this dynamic if available
            }
          ],
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Girimukti, Batujajar",
            "addressLocality": "Kabupaten Bandung Barat",
            "addressRegion": "Jawa Barat",
            "postalCode": "40561",
            "addressCountry": "ID"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+62-898-7459-759",
            "contactType": "customer service"
          }
        },
        {
          "@type": "AboutPage",
          "mainEntity": {
            "@id": "https://konveksiimoonusel.com/#organization"
          },
          "name": "Tentang iMoonusel Konveksi",
          "description": "Profil perusahaan konveksi Bandung yang berfokus pada kualitas dan ketepatan waktu."
        }
      ]
    };

    const script = document.createElement('script');
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/get_about_data.php`)   
      .then(res => res.json())      .then(result => {
        setData(result);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch about data", err);
        setLoading(false);
      });
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white text-black font-mono text-sm uppercase tracking-widest">
      Loading Data...
    </div>
  );

  if (!data) return null;

  const { content, stats, team } = data;
  
  // Helper to safely get content
  const getText = (key, lang = 'id') => content[key]?.[lang] || 'Loading...';
  const getStat = (key) => stats[key]?.id || '0';

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-blue-600 selection:text-white">
      
      <div className="pt-20">
        
        {/* HERO SECTION */}
        <section className="relative py-24 border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-8">
              <SectionTag>Our Manifesto</SectionTag>
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-12 uppercase">
                {getText('about_manifesto_title')}
              </h1>
              <p className="text-2xl text-zinc-800 font-light leading-relaxed max-w-3xl border-l-4 border-blue-600 pl-8">
                {getText('about_manifesto_text')}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col justify-end">
               <div className="text-right">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Established</p>
                 <p className="text-4xl font-black tracking-tight">2013</p>
               </div>
            </div>
          </div>
        </section>

        {/* STATS STRIP */}
        <section className="py-16 bg-zinc-50 border-b border-zinc-200">
           <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                 <StatItem label="Years Experience" value={getStat('exp')} />
                 <StatItem label="Expert Team" value={getStat('team')} />
                 <StatItem label="Machines Active" value={getStat('machines')} />
                 <StatItem label="Happy Clients" value={getStat('clients')} />
              </div>
           </div>
        </section>

        {/* PHILOSOPHY SECTION */}
        <section className="py-24">
           <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-24 items-center">
              <div>
                 <SectionTag>The Philosophy</SectionTag>
                 <h2 className="text-5xl font-black tracking-tighter uppercase mb-8 leading-none">
                    {getText('about_philosophy_title')}
                 </h2>
                 <p className="text-lg text-zinc-600 font-light leading-relaxed mb-8">
                    {getText('about_philosophy_text')}
                 </p>
                 <div className="flex flex-col gap-4">
                    {[
                        { icon: <Target className="w-5 h-5"/>, title: "Precision", text: "Setiap jahitan diukur dengan toleransi milimeter." },
                        { icon: <Zap className="w-5 h-5"/>, title: "Speed", text: "Optimalisasi alur kerja untuk lead time minimal." },
                        { icon: <Activity className="w-5 h-5"/>, title: "Scalability", text: "Siap menangani dari 100 hingga 100.000 unit." }
                    ].map((item, i) => (
                        <div key={i} className="flex items-start gap-4 p-4 border border-zinc-100 hover:border-zinc-300 transition-colors">
                           <div className="text-blue-600 mt-1">{item.icon}</div>
                           <div>
                              <h4 className="font-bold text-sm uppercase tracking-wide mb-1">{item.title}</h4>
                              <p className="text-sm text-zinc-500 font-light">{item.text}</p>
                           </div>
                        </div>
                    ))}
                 </div>
              </div>
              <div className="relative">
                 <div className="aspect-[4/5] bg-zinc-900 relative z-10 overflow-hidden">
                    <img 
                       src={getText('about_philosophy_image')} 
                       alt="Factory Floor" 
                       className="w-full h-full object-cover opacity-60 transition-all duration-700"
                    />
                    <div className="absolute bottom-0 left-0 w-full p-8 bg-gradient-to-t from-black to-transparent">
                       <p className="text-white text-xs font-mono uppercase">Factory Floor // Sector 7G</p>
                    </div>
                 </div>
                 <div className="absolute top-12 -right-12 w-full h-full border-2 border-zinc-200 -z-0 hidden md:block"></div>
              </div>
           </div>
        </section>

        {/* TEAM GRID */}
        <section className="py-24 bg-black text-white">
           <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                 <div>
                    <SectionTag>The Leadership</SectionTag>
                    <h2 className="text-5xl font-black tracking-tighter uppercase">Meet The Makers.</h2>
                 </div>
                 <p className="text-zinc-400 max-w-md text-sm leading-relaxed text-right md:text-left">
                    Tim inti yang memastikan visi anda terwujud dengan standar tertinggi.
                 </p>
              </div>

              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-1">
                 {team.map((member) => (
                    <div key={member.id} className="group relative bg-zinc-900 aspect-[3/4] overflow-hidden cursor-pointer">
                       {member.image_path ? (
                          <img 
                            src={member.image_path} 
                            alt={member.name} 
                            className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                          />
                       ) : (
                          <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-600">
                             <Users size={48} />
                          </div>
                       )}
                       
                       <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-90"></div>
                       
                       <div className="absolute bottom-0 left-0 w-full p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-2">
                             {member.position_id}
                          </p>
                          <h3 className="text-xl font-bold tracking-tight mb-2 text-white">
                             {member.name}
                          </h3>
                          <p className="text-xs text-zinc-400 italic line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity delay-100">
                             "{member.role_quote_id}"
                          </p>
                       </div>
                    </div>
                 ))}
              </div>
              
              <div className="mt-16 text-center">
                 <p className="text-zinc-500 text-sm mb-6 uppercase tracking-widest">And {getStat('team')} more skilled artisans behind the scenes.</p>
                 <BloombergButton variant="outline" className="mx-auto text-white border-white hover:bg-white hover:text-black">
                    Join Our Team
                 </BloombergButton>
              </div>
           </div>
        </section>

      </div>
    </div>
  );
};

export default About;

